import type {
  DomainCommandContext,
  DomainReadContext,
  JsonObject,
} from '../../contracts';
import type { Command } from '../../service';
import type { Entity } from '../../types';
import { knowledgeReviewers, sameCampus, scopeAllows } from './access';
import {
  entity,
  fail,
  need,
  planFor,
  preview,
  profileFor,
  safeEvidence,
  writeField,
} from './learning';
import type {
  AuthorizationScope,
  KnowledgeStore,
  KnowledgeVersion,
  KnowledgeView,
} from './types';
export function knowledgeStore(ctx: DomainReadContext): KnowledgeStore {
  const data = structuredClone(
    ctx.data(),
  ) as unknown as Partial<KnowledgeStore>;
  return {
    drafts: data.drafts || {},
    versions: data.versions || {},
    reuse: data.reuse || [],
    favorites: data.favorites || [],
    views: data.views || [],
  };
}
function save(ctx: DomainCommandContext, store: KnowledgeStore) {
  ctx.setData(JSON.parse(JSON.stringify(store)) as JsonObject);
}
function sourceCurrent(ctx: DomainReadContext, v: KnowledgeVersion) {
  const source = ctx.get(v.evidence.recordingUuid);
  return (
    !!safeEvidence(ctx, v.evidence) &&
    source?.revision === v.sourceRevision &&
    source.versions?.at(-1)?.uuid === v.evidence.transcriptVersionUuid
  );
}
function scopeOf(raw: string | undefined): AuthorizationScope {
  try {
    const s = JSON.parse(raw || '{}');
    if (
      !Array.isArray(s.campuses) ||
      !s.campuses.length ||
      s.campuses.some((v: unknown) => typeof v !== 'string') ||
      !Array.isArray(s.roles) ||
      s.roles.some(
        (v: unknown) =>
          ![
            'teacher',
            'researchLead',
            'campusManager',
            'groupOperator',
            'regionManager',
          ].includes(String(v)),
      ) ||
      !Array.isArray(s.memberUuids) ||
      s.memberUuids.some((v: unknown) => typeof v !== 'string') ||
      !s.basis?.trim()
    )
      throw Error();
    return s;
  } catch {
    return fail('请设置完整的校区、角色、成员授权范围及依据');
  }
}
export function validateKnowledge(ctx: DomainReadContext, v: KnowledgeVersion) {
  for (const key of [
    'title',
    'subject',
    'stage',
    'scene',
    'topic',
    'situation',
    'steps',
    'conditions',
    'limitations',
    'body',
  ] as const)
    need(v[key], key);
  if (!v.redacted || !v.sourceVerified) fail('必须完成来源核实与脱敏检查');
  scopeOf(JSON.stringify(v.scope));
  if (!sourceCurrent(ctx, v))
    fail('来源版本或授权已变化，请重新核实并保存草稿', 'CONFLICT');
  const text = [
    v.title,
    v.situation,
    v.steps,
    v.conditions,
    v.limitations,
    v.body,
  ].join('\n');
  if (
    /\b1[3-9]\d{9}\b|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}|\b\d{17}[\dXx]\b/.test(text)
  )
    fail('内容含可能的电话、邮箱或证件信息，请先脱敏');
  if (v.sourceConclusionUuid) {
    const conclusion = ctx.get(v.sourceConclusionUuid);
    if (
      !conclusion ||
      !['confirmed', 'resolved', 'closed'].includes(conclusion.status) ||
      conclusion.revision !== v.sourceConclusionRevision
    )
      fail('所引用核查结论尚未确认或版本已变化');
  }
}
export function knowledgeQuery(ctx: DomainReadContext): KnowledgeView[] {
  const store = knowledgeStore(ctx);
  return ctx
    .list('knowledge')
    .filter((r) => r.fields?.learningManaged === 'true')
    .flatMap((row) => {
      const editor =
        sameCampus(row, ctx.context) &&
        row.fields?.authorView === ctx.context.viewMemberUuid;
      const reviewer =
        sameCampus(row, ctx.context) &&
        knowledgeReviewers.includes(ctx.context.role);
      const versions = store.versions[row.uuid] || [];
      const published = versions.find(
        (v) =>
          v.uuid === row.fields?.publishedVersionUuid &&
          v.status === 'published',
      );
      const readable = published && scopeAllows(published.scope, ctx.context);
      const historical = versions.some(
        (v) =>
          v.status === 'withdrawn' &&
          scopeAllows(v.scope, ctx.context) &&
          store.reuse.some(
            (r) =>
              r.versionUuid === v.uuid &&
              (r.actor === ctx.context.viewMemberUuid ||
                r.targetMemberUuid === ctx.context.viewMemberUuid),
          ),
      );
      if (!editor && !reviewer && !readable && !historical) return [];
      const project = (v: KnowledgeVersion): KnowledgeVersion => ({
        ...v,
        evidence: { ...v.evidence, quote: '' },
      });
      const draft = editor || reviewer ? store.drafts[row.uuid] : undefined;
      const selected =
        draft ||
        published ||
        versions
          .filter(
            (v) =>
              v.status === 'withdrawn' && scopeAllows(v.scope, ctx.context),
          )
          .at(-1);
      const accessible = !!(selected && safeEvidence(ctx, selected.evidence));
      const source = selected
        ? ctx.get(selected.evidence.recordingUuid)
        : undefined;
      return [
        {
          entity: row,
          activity: {
            views: store.views.filter(
              (v) =>
                v.knowledgeUuid === row.uuid &&
                v.actor === ctx.context.viewMemberUuid,
            ).length,
            favorites: store.favorites.filter(
              (v) =>
                v.knowledgeUuid === row.uuid &&
                v.actor === ctx.context.viewMemberUuid,
            ).length,
            reuse: store.reuse.filter(
              (v) =>
                v.knowledgeUuid === row.uuid &&
                v.actor === ctx.context.viewMemberUuid,
            ).length,
          },
          published:
            published && (readable || editor || reviewer)
              ? project(published)
              : undefined,
          draft: draft ? project(draft) : undefined,
          versions: versions
            .filter(
              (v) =>
                editor ||
                reviewer ||
                (['published', 'withdrawn'].includes(v.status) &&
                  scopeAllows(v.scope, ctx.context)),
            )
            .map(project),
          reuse: store.reuse.filter(
            (r) =>
              r.knowledgeUuid === row.uuid &&
              (r.actor === ctx.context.viewMemberUuid || reviewer),
          ),
          favorite: store.favorites.some(
            (f) =>
              f.knowledgeUuid === row.uuid &&
              f.actor === ctx.context.viewMemberUuid,
          ),
          canEdit: editor,
          canReview: reviewer && draft?.author !== ctx.context.viewMemberUuid,
          sourceAccessible: accessible,
          sourceChanged:
            row.fields?.sourceChanged === 'true' ||
            !!(
              source &&
              selected &&
              (source.revision !== selected.sourceRevision ||
                source.versions?.at(-1)?.uuid !==
                  selected.evidence.transcriptVersionUuid)
            ),
        },
      ];
    });
}
export function knowledgeCommand(
  ctx: DomainCommandContext,
  input: Command,
): Entity {
  preview(input);
  const f = input.fields || {};
  const op = input.action.split('.').at(-1);
  const store = knowledgeStore(ctx);
  if (op === 'createDraft') {
    const evidence = safeEvidence(ctx, input.evidence);
    if (!evidence) fail('请先选择有权访问的实际来源片段');
    const source = entity(ctx, evidence.recordingUuid, 'recording');
    const row = ctx.append('knowledge', need(input.title, '案例标题'), {
      status: 'draft',
      campus: source.campus,
      memberUuid: ctx.context.viewMemberUuid,
      fields: {
        learningManaged: 'true',
        authorView: ctx.context.viewMemberUuid,
      },
    });
    store.drafts[row.uuid] = {
      uuid: crypto.randomUUID(),
      knowledgeUuid: row.uuid,
      version: 1,
      revision: 1,
      author: ctx.context.viewMemberUuid,
      createdAt: ctx.now,
      title: row.title,
      subject: '',
      stage: '',
      scene: '',
      topic: '',
      situation: '',
      steps: '',
      conditions: '',
      limitations: '',
      body: '',
      evidence: { ...evidence, quote: '' },
      sourceRevision: source.revision,
      sourceVerified: false,
      redacted: false,
      scope: {
        campuses: [source.campus],
        roles: [],
        memberUuids: [],
        basis: '',
      },
      status: 'draft',
    };
    save(ctx, store);
    return row;
  }
  const row = entity(ctx, input.uuid, 'knowledge');
  if (row.fields?.learningManaged !== 'true')
    fail('历史案例须重新核实来源后建立审核草稿');
  const author =
    row.fields.authorView === ctx.context.viewMemberUuid &&
    sameCampus(row, ctx.context);
  const reviewer =
    knowledgeReviewers.includes(ctx.context.role) &&
    sameCampus(row, ctx.context);
  let draft = store.drafts[row.uuid];
  const versions = store.versions[row.uuid] || [];
  const published = versions.find(
    (v) =>
      v.uuid === row.fields?.publishedVersionUuid && v.status === 'published',
  );
  if (op === 'newVersion') {
    if (!author || !published || draft)
      fail('只有作者可为已发布案例建立一个新草稿');
    draft = {
      ...structuredClone(published),
      uuid: crypto.randomUUID(),
      version: Math.max(...versions.map((v) => v.version), 0) + 1,
      revision: 1,
      createdAt: ctx.now,
      status: 'draft',
      sourceVerified: false,
      reviewer: undefined,
      reviewedAt: undefined,
      reviewNote: undefined,
    };
    store.drafts[row.uuid] = draft;
  } else if (op === 'saveDraft' || op === 'submit') {
    if (!author || !draft || draft.status === 'submitted')
      fail('仅作者可编辑未送审草稿', 'FORBIDDEN');
    if (op === 'saveDraft') {
      for (const key of [
        'title',
        'subject',
        'stage',
        'scene',
        'topic',
        'situation',
        'steps',
        'conditions',
        'limitations',
        'body',
      ] as const)
        if (f[key] !== undefined) draft[key] = f[key];
      draft.scope = scopeOf(f.scope);
      draft.redacted = f.redacted === 'true';
      draft.sourceVerified = f.sourceVerified === 'true';
      if (input.evidence) {
        const e = safeEvidence(ctx, input.evidence);
        if (!e) fail('来源片段不可用');
        draft.evidence = { ...e, quote: '' };
      }
      if (draft.sourceVerified) {
        const source = entity(ctx, draft.evidence.recordingUuid, 'recording');
        if (
          source.versions?.at(-1)?.uuid !== draft.evidence.transcriptVersionUuid
        )
          fail('请选用当前转写版本重新核实');
        draft.sourceRevision = source.revision;
      }
      draft.revision++;
      draft.status = 'draft';
      if (!published) row.title = draft.title;
    } else {
      validateKnowledge(ctx, draft);
      draft.status = 'submitted';
      draft.revision++;
      if (!published) row.status = 'pendingReview';
      ctx.notify(row, '教学案例待另一位审核人审核');
    }
  } else if (op === 'review') {
    if (
      !reviewer ||
      !draft ||
      draft.status !== 'submitted' ||
      draft.author === ctx.context.viewMemberUuid
    )
      fail('须由另一位授权审核人审核待审版本', 'FORBIDDEN');
    if (f.versionUuid !== draft.uuid) fail('待审版本已变化', 'CONFLICT');
    const note = need(input.note, '审核意见');
    if (!['publish', 'return'].includes(f.decision)) fail('请选择发布或退回');
    if (f.decision === 'publish') {
      if (
        f.privacyChecked !== 'true' ||
        f.authorizationChecked !== 'true' ||
        f.sourceChecked !== 'true'
      )
        fail('请逐项完成隐私、授权与来源审核');
      validateKnowledge(ctx, draft);
      const approved = {
        ...structuredClone(draft),
        status: 'published' as const,
        reviewer: ctx.context.viewMemberUuid,
        reviewedAt: ctx.now,
        reviewNote: note,
      };
      versions.push(approved);
      store.versions[row.uuid] = versions;
      delete store.drafts[row.uuid];
      row.status = 'published';
      row.title = approved.title;
      row.note = approved.body;
      row.evidence = { ...approved.evidence, quote: '' };
      row.fields = {
        ...row.fields,
        publishedVersionUuid: approved.uuid,
        publishedScope: JSON.stringify(approved.scope),
        sourceChanged: 'false',
      };
      ctx.notify(row, '案例已审核发布');
    } else {
      draft.status = 'returned';
      draft.reviewNote = note;
      draft.reviewer = ctx.context.viewMemberUuid;
      draft.reviewedAt = ctx.now;
      if (!published) row.status = 'returned';
      ctx.notify(row, '案例已退回补充');
    }
  } else if (op === 'withdraw') {
    if (!reviewer || !published)
      fail('仅授权审核人可撤回已发布版本', 'FORBIDDEN');
    const note = need(input.note, '撤回依据');
    published.status = 'withdrawn';
    published.reviewNote = note;
    row.status = 'withdrawn';
    row.fields = {
      ...row.fields,
      sourceChanged: 'true',
      historicalMemberUuids: JSON.stringify([
        ...new Set(
          store.reuse
            .filter((r) => r.knowledgeUuid === row.uuid)
            .flatMap((r) => [r.actor, r.targetMemberUuid].filter(Boolean)),
        ),
      ]),
    };
    for (const p of ctx.list('plan')) {
      if (
        planFor(p)?.references.some((r) => r.versionUuid === published.uuid)
      ) {
        p.fields = {
          ...p.fields,
          knowledgeImpact: `引用案例 v${published.version} 已撤回：${note}`,
        };
        ctx.touch(p);
        ctx.notify(p, '引用案例已撤回，请复核实践适用性');
      }
    }
    ctx.notify(row, '案例已撤回，禁止新的检索与复用');
  } else if (['favorite', 'view', 'reuse', 'feedback'].includes(op || '')) {
    if (op === 'feedback') {
      const reuse = store.reuse.find(
        (r) =>
          r.uuid === f.reuseUuid &&
          r.knowledgeUuid === row.uuid &&
          r.actor === ctx.context.viewMemberUuid,
      );
      if (!reuse || !['applicable', 'notApplicable'].includes(f.feedback || ''))
        fail('请针对本人实际引用记录填写适用反馈');
      reuse.feedback = f.feedback as 'applicable' | 'notApplicable';
      reuse.note = need(input.note, '适用条件反馈');
    } else {
      if (
        !published ||
        row.status !== 'published' ||
        !scopeAllows(published.scope, ctx.context) ||
        row.fields?.sourceChanged === 'true'
      )
        fail('此案例版本尚未发布、已撤回、来源变化或超出授权范围', 'FORBIDDEN');
      if (f.versionUuid !== published.uuid)
        fail('发布版本已变化，请重新预览', 'CONFLICT');
      if (op === 'favorite') {
        const i = store.favorites.findIndex(
          (v) =>
            v.knowledgeUuid === row.uuid &&
            v.actor === ctx.context.viewMemberUuid,
        );
        if (i < 0)
          store.favorites.push({
            knowledgeUuid: row.uuid,
            actor: ctx.context.viewMemberUuid,
          });
        else store.favorites.splice(i, 1);
      } else if (op === 'view') {
        if (
          !store.views.some(
            (v) =>
              v.knowledgeUuid === row.uuid &&
              v.versionUuid === published.uuid &&
              v.actor === ctx.context.viewMemberUuid &&
              v.at.slice(0, 10) === ctx.now.slice(0, 10),
          )
        )
          store.views.push({
            knowledgeUuid: row.uuid,
            versionUuid: published.uuid,
            actor: ctx.context.viewMemberUuid,
            at: ctx.now,
          });
      } else {
        if (!['plan', 'training'].includes(f.kind))
          fail('请选择计划引用或培训使用');
        const target = entity(
          ctx,
          f.targetUuid,
          f.kind === 'plan' ? 'plan' : 'teacher',
        );
        if (
          target.memberUuid !== ctx.context.viewMemberUuid &&
          ![
            'campusManager',
            'groupOperator',
            'researchLead',
            'regionManager',
          ].includes(ctx.context.role)
        )
          fail('无权替该成员引用案例', 'FORBIDDEN');
        if (String(target.revision) !== f.targetRevision)
          fail('引用目标已变化，请重新预览', 'CONFLICT');
        const existing = store.reuse.find(
          (r) =>
            r.knowledgeUuid === row.uuid &&
            r.versionUuid === published.uuid &&
            r.targetUuid === target.uuid &&
            r.kind === f.kind,
        );
        if (!existing) {
          const reuse = {
            uuid: crypto.randomUUID(),
            knowledgeUuid: row.uuid,
            versionUuid: published.uuid,
            targetUuid: target.uuid,
            targetMemberUuid: target.memberUuid,
            kind: f.kind as 'plan' | 'training',
            actor: ctx.context.viewMemberUuid,
            at: ctx.now,
          };
          if (f.kind === 'plan') {
            const plan = planFor(target);
            if (!plan || ['completed', 'cancelled'].includes(target.status))
              fail('请选择有效正式实践计划');
            plan.references.push({
              knowledgeUuid: row.uuid,
              versionUuid: published.uuid,
              reuseUuid: reuse.uuid,
              title: published.title,
            });
            writeField(target, 'learningPlan', plan);
          } else {
            const profile = profileFor(target);
            profile.training.push({
              uuid: reuse.uuid,
              title: published.title,
              at: ctx.now,
              feedback: need(input.note, '培训使用安排（不等于完成实践）'),
              sourceKnowledgeUuid: row.uuid,
              versionUuid: published.uuid,
            });
            writeField(target, 'learningProfile', profile);
          }
          store.reuse.push(reuse);
          ctx.touch(target);
          ctx.notify(target, `已引用案例 v${published.version}`);
        }
      }
    }
  } else return fail('未知知识操作');
  save(ctx, store);
  ctx.touch(row);
  return row;
}
